import type { LscLeague, LscMember } from './types';

export type EffectiveLeagueRole = 'owner' | 'commissioner' | 'member';

export const LEAGUE_ROLE_OWNER: EffectiveLeagueRole = 'owner';
export const LEAGUE_ROLE_COMMISSIONER: EffectiveLeagueRole = 'commissioner';
export const LEAGUE_ROLE_MEMBER: EffectiveLeagueRole = 'member';

export const OPERATOR_STORED_ROLES = new Set(['owner', 'commissioner', 'admin']);

export function normalizeStoredRole(role?: string | null): string {
  return String(role || LEAGUE_ROLE_MEMBER).trim().toLowerCase();
}

export function getEffectiveLeagueRole(opts: {
  league?: Pick<LscLeague, 'commissioner_uid'> | null;
  membership?: Pick<LscMember, 'role' | 'status'> | null;
  userUid?: string | null;
  effectiveRole?: string | null;
}): EffectiveLeagueRole {
  if (opts.effectiveRole) {
    const normalized = normalizeStoredRole(opts.effectiveRole);
    if (normalized === LEAGUE_ROLE_OWNER) return LEAGUE_ROLE_OWNER;
    if (normalized === LEAGUE_ROLE_COMMISSIONER || normalized === 'admin') {
      return LEAGUE_ROLE_COMMISSIONER;
    }
    return LEAGUE_ROLE_MEMBER;
  }

  const uid = String(opts.userUid || '').trim();
  const ownerUid = String(opts.league?.commissioner_uid || '').trim();
  if (uid && ownerUid && uid === ownerUid) {
    return LEAGUE_ROLE_OWNER;
  }

  const stored = normalizeStoredRole(opts.membership?.role);
  if (stored === LEAGUE_ROLE_OWNER && uid && ownerUid && uid === ownerUid) {
    return LEAGUE_ROLE_OWNER;
  }
  if (stored === LEAGUE_ROLE_COMMISSIONER || stored === 'admin') {
    return LEAGUE_ROLE_COMMISSIONER;
  }
  return LEAGUE_ROLE_MEMBER;
}

export function isLeagueOwner(role: EffectiveLeagueRole): boolean {
  return role === LEAGUE_ROLE_OWNER;
}

export function isLeagueCommissioner(role: EffectiveLeagueRole): boolean {
  return role === LEAGUE_ROLE_COMMISSIONER;
}

export function isLeagueOperator(role: EffectiveLeagueRole): boolean {
  return role === LEAGUE_ROLE_OWNER || role === LEAGUE_ROLE_COMMISSIONER;
}

export function leagueRoleLabel(role: EffectiveLeagueRole): string {
  if (role === LEAGUE_ROLE_OWNER) return 'League Owner';
  if (role === LEAGUE_ROLE_COMMISSIONER) return 'Commissioner';
  return 'League Member';
}

export function leagueRoleBadgeClass(role: EffectiveLeagueRole): string {
  if (role === LEAGUE_ROLE_OWNER) return 'text-amber-300';
  if (role === LEAGUE_ROLE_COMMISSIONER) return 'text-primary-300';
  return 'text-secondary-400';
}

/** Customer-facing roster role label; maps legacy admin to Commissioner. */
export function memberRoleDisplayLabel(
  member: Pick<LscMember, 'role' | 'user_uid'>,
  ownerUid?: string | null
): string {
  const role = getEffectiveLeagueRole({
    membership: member,
    userUid: member.user_uid,
    league: ownerUid ? { commissioner_uid: ownerUid } : undefined,
  });
  return leagueRoleLabel(role);
}

export const COMMISSIONER_STAFF_COPY =
  'Commissioners can help manage streams, reviews, archives, and roster upkeep. Billing and owner settings stay with the League Owner.';

export const OWNER_ONLY_SETTINGS_COPY =
  'Only the League Owner can manage billing and owner settings.';
